import { HarborClient } from "./data/client";
import { preloadTheaterAssets } from "./sprites/assets";
import { Theater } from "./theater/Theater";
import {
  mergeTxDetail,
  renderBlockDetailHtml,
  renderTxDetailHtml,
  type EntityDetail,
} from "./ui/detail";
import "./styles/main.css";

void preloadTheaterAssets();

const EXPLAINER_KEY = "archipelago.explainer.dismissed";

const params = new URLSearchParams(location.search);
const network = params.get("network") === "mainnet" ? "mainnet" : "testnet";
const demo = params.get("demo") === "1";

const boot = document.getElementById("boot")!;
const canvas = document.getElementById("theater") as HTMLCanvasElement;
const hud = document.getElementById("hud")!;
const muteBtn = document.getElementById("mute") as HTMLButtonElement;
const pauseBtn = document.getElementById("pause") as HTMLButtonElement;
const collapseBtn = document.getElementById("hud-collapse") as HTMLButtonElement;
const networkBadge = document.getElementById("network-badge")!;
const explainer = document.getElementById("explainer")!;
const detailDrawer = document.getElementById("detail-drawer")!;
const detailTitle = document.getElementById("detail-title")!;
const detailBody = document.getElementById("detail-body")!;
const infoDrawer = document.getElementById("info-drawer")!;
const a11yStatus = document.getElementById("a11y-status")!;
const statusPending = document.getElementById("status-pending")!;
const statusHeight = document.getElementById("status-height")!;
const statusStream = document.getElementById("status-stream")!;
const statusEvent = document.getElementById("status-event")!;
const hudStreamAge = document.getElementById("hud-stream-age")!;

networkBadge.textContent = network === "mainnet" ? "MAINNET" : "TESTNET";
networkBadge.setAttribute(
  "aria-label",
  `Network: ${network === "mainnet" ? "mainnet" : "testnet"}`,
);

let lastEventAt = 0;
let announceTimer: number | null = null;
let pendingAnnounce = "";

function formatStreamAge(msAgo: number): string {
  if (msAgo < 1_500) return "live, event just now";
  if (msAgo < 60_000) return `live, event ${Math.floor(msAgo / 1000)}s ago`;
  return `live, event ${Math.floor(msAgo / 60_000)}m ago`;
}

function refreshStreamAge(): void {
  const streamEl = document.getElementById("hud-stream")!;
  const on = streamEl.classList.contains("stream-on");
  if (!on) {
    hudStreamAge.textContent = "";
    statusStream.textContent = "stream · off";
    return;
  }
  if (!lastEventAt) {
    hudStreamAge.textContent = "live";
    statusStream.textContent = "stream · live";
    return;
  }
  const age = formatStreamAge(Date.now() - lastEventAt);
  hudStreamAge.textContent = age;
  statusStream.textContent = `stream · ${age}`;
}

window.setInterval(refreshStreamAge, 1000);

function throttleAnnounce(text: string): void {
  pendingAnnounce = text;
  statusEvent.textContent = text;
  if (announceTimer != null) return;
  announceTimer = window.setTimeout(() => {
    announceTimer = null;
    a11yStatus.textContent = pendingAnnounce;
  }, 1200);
}

const theater = new Theater(
  canvas,
  {
    setPending(n) {
      document.getElementById("hud-pending")!.textContent = String(n);
      statusPending.textContent = `shore · ${n} waiting transaction${n === 1 ? "" : "s"}`;
    },
    setHeight(h) {
      // Coerce to a single tip label — never a coalesced / wrapping list.
      let label = String(h).trim();
      if (!label.startsWith("#") && /^\d+$/.test(label)) {
        label = `#${Number(label).toLocaleString("en-US")}`;
      } else {
        const tips = label.match(/#\d[\d,]*/g);
        if (tips && tips.length > 0) label = tips[tips.length - 1]!;
      }
      document.getElementById("hud-height")!.textContent = label;
      statusHeight.textContent = `latest block · ${label === "—" ? "—" : label}`;
      lastEventAt = Date.now();
      refreshStreamAge();
    },
    setStream(on) {
      const el = document.getElementById("hud-stream")!;
      el.textContent = on ? "● live" : "off";
      el.classList.toggle("stream-on", on);
      el.classList.toggle("stream-off", !on);
      refreshStreamAge();
    },
    toast(text) {
      const el = document.getElementById("hud-toast")!;
      el.textContent = text;
      window.clearTimeout((el as HTMLElement & { _t?: number })._t);
      (el as HTMLElement & { _t?: number })._t = window.setTimeout(() => {
        el.textContent = "";
      }, 3200);
      lastEventAt = Date.now();
      refreshStreamAge();
    },
    announce(text) {
      throttleAnnounce(text);
      lastEventAt = Date.now();
      refreshStreamAge();
    },
  },
  network,
);

theater.setSelectHandler((detail) => openDetail(detail));
theater.start();
hud.hidden = false;

/** Mobile starts collapsed — only latest block visible. */
const mobile = window.matchMedia("(max-width: 720px)").matches;
if (mobile) {
  hud.classList.add("collapsed");
  collapseBtn.setAttribute("aria-expanded", "false");
  collapseBtn.setAttribute("aria-label", "Expand panel");
  collapseBtn.textContent = "▴";
}

collapseBtn.addEventListener("click", () => {
  const collapsed = hud.classList.toggle("collapsed");
  collapseBtn.setAttribute("aria-expanded", collapsed ? "false" : "true");
  collapseBtn.setAttribute(
    "aria-label",
    collapsed ? "Expand panel" : "Collapse panel",
  );
  collapseBtn.textContent = collapsed ? "▴" : "▾";
  collapseBtn.title = collapsed ? "Expand panel" : "Collapse panel";
});

let muted = true;
muteBtn.title = "Sound off — click to unmute";
muteBtn.setAttribute("aria-label", "Unmute sound");
muteBtn.addEventListener("click", () => {
  muted = !muted;
  theater.setMuted(muted);
  muteBtn.textContent = muted ? "♪" : "♫";
  muteBtn.classList.toggle("on", !muted);
  muteBtn.title = muted
    ? "Sound off — click to unmute"
    : "Sound on — click to mute";
  muteBtn.setAttribute("aria-label", muted ? "Unmute sound" : "Mute sound");
});

function syncPauseUi(): void {
  const paused = theater.isPaused;
  pauseBtn.setAttribute("aria-pressed", paused ? "true" : "false");
  pauseBtn.setAttribute(
    "aria-label",
    paused ? "Resume animation" : "Pause animation",
  );
  pauseBtn.title = paused ? "Resume animation" : "Pause animation";
  pauseBtn.textContent = paused ? "▶" : "❚❚";
  pauseBtn.classList.toggle("on", paused);
}

syncPauseUi();
pauseBtn.addEventListener("click", () => {
  theater.togglePaused();
  syncPauseUi();
});

/* ——— First-visit explainer ——— */
if (!localStorage.getItem(EXPLAINER_KEY)) {
  explainer.hidden = false;
}
document.getElementById("explainer-dismiss")!.addEventListener("click", () => {
  localStorage.setItem(EXPLAINER_KEY, "1");
  explainer.hidden = true;
});

/* ——— Info drawer ——— */
document.getElementById("info-open")!.addEventListener("click", () => {
  infoDrawer.hidden = false;
});

function closeDrawers(which?: "detail" | "info"): void {
  if (!which || which === "detail") detailDrawer.hidden = true;
  if (!which || which === "info") infoDrawer.hidden = true;
}

document.querySelectorAll("[data-close]").forEach((el) => {
  el.addEventListener("click", () => {
    const which = (el as HTMLElement).dataset.close as "detail" | "info";
    closeDrawers(which);
  });
});

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape") closeDrawers();
});

let harborClient: HarborClient | null = null;
let detailSeq = 0;

function wireCopyButtons(root: HTMLElement): void {
  root.querySelectorAll<HTMLButtonElement>("[data-copy]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const value = btn.dataset.copy ?? "";
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        const prev = btn.textContent;
        btn.textContent = "copied";
        window.setTimeout(() => {
          btn.textContent = prev;
        }, 1200);
      } catch {
        btn.textContent = "fail";
      }
    });
  });
}

function paintDetail(detail: EntityDetail, enriching = false): void {
  if (detail.kind === "tx") {
    detailTitle.textContent = detail.name;
    detailBody.innerHTML = renderTxDetailHtml(detail, { enriching });
  } else {
    detailTitle.textContent = `Block ${detail.label}`;
    detailBody.innerHTML = renderBlockDetailHtml(detail);
  }
  wireCopyButtons(detailBody);
}

function openDetail(detail: EntityDetail): void {
  const seq = ++detailSeq;
  paintDetail(detail, detail.kind === "tx" && !detail.programId);
  detailDrawer.hidden = false;

  if (detail.kind !== "tx" || !harborClient) return;
  void harborClient.fetchTx(detail.txid, network).then((rest) => {
    if (seq !== detailSeq || detailDrawer.hidden || !rest) {
      if (seq === detailSeq && detail.kind === "tx" && !detail.programId) {
        paintDetail(detail, false);
      }
      return;
    }
    paintDetail(mergeTxDetail(detail, rest), false);
  });
}

async function bootLive(): Promise<void> {
  const client = new HarborClient();
  harborClient = client;
  client.on((e) => theater.handle(e));

  try {
    const snap = await client.fetchSnapshot(network);
    theater.handle({ type: "snapshot", data: snap });
  } catch (err) {
    console.warn("snapshot failed; starting ws anyway", err);
  }

  client.connect(network);
  boot.remove();
}

function bootDemo(): void {
  boot.remove();
  theater.setMuted(true);
  theater.handle({
    type: "status",
    data: { connected: true, detail: "demo" },
  });

  let height = 39_500_000;
  // Demo mirrors live: dragons only when a successful tx names a block_height.
  const spawn = () => {
    height += 1;
    theater.handle({
      type: "block",
      data: {
        hash: cryptoRandomHex(32),
        height,
        timestamp: new Date().toISOString(),
        transaction_count: 1,
      },
    });
    theater.handle({
      type: "transaction",
      data: {
        txid: cryptoRandomHex(32),
        height,
        success: true,
      },
    });
  };

  spawn();
  window.setInterval(spawn, 4000);
  // Quiet tip advances without summoning empty dragons.
  window.setInterval(() => {
    height += 1;
    theater.handle({
      type: "block",
      data: {
        hash: cryptoRandomHex(32),
        height,
        timestamp: new Date().toISOString(),
        transaction_count: 0,
      },
    });
  }, 7000);
}

function cryptoRandomHex(bytes: number): string {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return [...arr].map((b) => b.toString(16).padStart(2, "0")).join("");
}

if (demo) bootDemo();
else void bootLive();
